import { useCallback, useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { useCandidatesPaged } from "../../hooks/useCandidatesPaged"
import { useDistinctSkills } from "../../hooks/useDistinctSkills"
import { CandidateFilterBar } from "../components/CandidateFilterBar"
import { CandidateProfileSheet } from "../components/CandidateProfileSheet"
import {
  CandidatesTable,
  CandidatesTableSkeleton,
} from "../components/CandidatesTable"
import type {
  GetCandidatesPagedParams,
  VacancyStatus,
  ApplicationStage,
  CandidateSkillsMatchMode,
  CandidateSortField,
  SortDirection,
} from "../../infrastructure/candidate-service"

const DEFAULT_PAGE_SIZE = 20
const SELECTED_CANDIDATE_PARAM = "candidateId"
const SEARCH_DEBOUNCE_MS = 300

function parseFiltersFromParams(params: URLSearchParams): GetCandidatesPagedParams {
  const get = (key: string) => params.get(key) ?? undefined
  const getNum = (key: string) => {
    const v = params.get(key)
    return v !== null ? Number(v) : undefined
  }
  const getBool = (key: string) => {
    const v = params.get(key)
    return v === "true" ? true : v === "false" ? false : undefined
  }
  const getArray = (key: string) => {
    const values = params.getAll(key)
    return values.length > 0 ? values : undefined
  }

  return {
    vacancyIds: getArray("vacancyIds"),
    vacancyStatus: get("vacancyStatus") as VacancyStatus | undefined,
    applicationStage: get("applicationStage") as ApplicationStage | undefined,
    skills: getArray("skills"),
    skillsMatchMode: (get("skillsMatchMode") as CandidateSkillsMatchMode | undefined) ?? "Any",
    minExperienceYears: getNum("minExperienceYears"),
    maxExperienceYears: getNum("maxExperienceYears"),
    location: get("location"),
    hasCv: getBool("hasCv"),
    sortBy: (get("sortBy") as CandidateSortField | undefined) ?? "Name",
    sortDirection: (get("sortDirection") as SortDirection | undefined) ?? "Asc",
    page: getNum("page") ?? 1,
    pageSize: getNum("pageSize") ?? DEFAULT_PAGE_SIZE,
  }
}

function filtersToSearchParams(
  filters: GetCandidatesPagedParams,
  search: string,
): URLSearchParams {
  const params = new URLSearchParams()

  if (search) params.set("search", search)
  if (filters.vacancyIds?.length) {
    filters.vacancyIds.forEach((id) => params.append("vacancyIds", id))
  }
  if (filters.vacancyStatus) params.set("vacancyStatus", filters.vacancyStatus)
  if (filters.applicationStage) params.set("applicationStage", filters.applicationStage)
  if (filters.skills?.length) {
    filters.skills.forEach((s) => params.append("skills", s))
    params.set("skillsMatchMode", filters.skillsMatchMode ?? "Any")
  }
  if (filters.minExperienceYears !== undefined)
    params.set("minExperienceYears", String(filters.minExperienceYears))
  if (filters.maxExperienceYears !== undefined)
    params.set("maxExperienceYears", String(filters.maxExperienceYears))
  if (filters.location) params.set("location", filters.location)
  if (filters.hasCv !== undefined) params.set("hasCv", String(filters.hasCv))
  if (filters.sortBy && filters.sortBy !== "Name") params.set("sortBy", filters.sortBy)
  if (filters.sortDirection && filters.sortDirection !== "Asc")
    params.set("sortDirection", filters.sortDirection)
  if (filters.page && filters.page !== 1) params.set("page", String(filters.page))
  if (filters.pageSize && filters.pageSize !== DEFAULT_PAGE_SIZE)
    params.set("pageSize", String(filters.pageSize))

  return params
}

export function CandidatesListPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const initialFilters = parseFiltersFromParams(searchParams)
  const initialSearch = searchParams.get("search") ?? ""

  const [filters, setFilters] = useState<GetCandidatesPagedParams>(initialFilters)
  const [searchValue, setSearchValue] = useState(initialSearch)
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue)
      setFilters((prev) => ({ ...prev, page: 1 }))
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchValue])

  // The open candidate lives in the URL (?candidateId=) so it is linkable and Back closes the panel.
  const selectedCandidateId = searchParams.get(SELECTED_CANDIDATE_PARAM)

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = filtersToSearchParams(filters, debouncedSearch)
        const selected = prev.get(SELECTED_CANDIDATE_PARAM)
        if (selected) next.set(SELECTED_CANDIDATE_PARAM, selected)
        return next
      },
      { replace: true },
    )
  }, [filters, debouncedSearch, setSearchParams])

  const handleSelectCandidate = useCallback(
    (candidateId: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set(SELECTED_CANDIDATE_PARAM, candidateId)
        return next
      })
    },
    [setSearchParams],
  )

  const handleCloseCandidate = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete(SELECTED_CANDIDATE_PARAM)
      return next
    })
  }, [setSearchParams])

  const queryParams: GetCandidatesPagedParams = {
    ...filters,
    search: debouncedSearch || undefined,
  }

  const { data, isLoading, isError } = useCandidatesPaged(queryParams)
  const { data: skillsData } = useDistinctSkills()

  useEffect(() => {
    if (isError) {
      toast.error("Could not load candidates. Please try again.")
    }
  }, [isError])

  const availableSkills = skillsData?.skills ?? []

  const handleFiltersChange = useCallback(
    (next: Partial<GetCandidatesPagedParams>) => {
      setFilters((prev) => ({ ...prev, ...next, page: 1 }))
    },
    [],
  )

  const handleClearFilters = useCallback(() => {
    setFilters({
      sortBy: "Name",
      sortDirection: "Asc",
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    })
    setSearchValue("")
    setDebouncedSearch("")
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }, [])

  const candidates = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const currentPage = filters.page ?? 1
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE

  return (
    <div className="p-2 sm:p-4 space-y-4 sm:space-y-6 min-w-0">
      <div className="bg-card rounded-lg border border-border p-4 sm:p-6 lg:p-8 shadow-sm">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Candidates</h1>
            <p className="text-sm text-muted-foreground mt-1">
              All candidates across every vacancy in your organisation.
            </p>
          </div>
        </div>

        <CandidateFilterBar
          filters={filters}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          availableSkills={availableSkills}
          totalCount={totalCount}
        />
      </div>

      {isLoading ? (
        <CandidatesTableSkeleton />
      ) : isError ? (
        <p className="text-sm text-destructive py-4">
          Could not load candidates. Please try again.
        </p>
      ) : (
        <CandidatesTable
          candidates={candidates}
          totalCount={totalCount}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onSelectCandidate={handleSelectCandidate}
        />
      )}

      <CandidateProfileSheet
        candidateId={selectedCandidateId}
        onClose={handleCloseCandidate}
      />
    </div>
  )
}
